import { AccessorFnColumnDef, ColumnHelper, type Table as TanstackTable } from "@tanstack/react-table";
import debounce from "lodash/debounce";
import entries from "lodash/entries";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import map from "lodash/map";
import mapValues from "lodash/mapValues";
import merge from "lodash/merge";
import reduce from "lodash/reduce";
import { FIELD_AVAILABILITY_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import {
  useMultiActivityFormStore,
  useMultiActivityFormStoreActions
} from "PFApp/stores/activity_form/multi_roles_form";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import Table, { ClearEvent, DragAxis, FillEvent, InteractiveTable, PasteEvent } from "PFComponents/table";
import { stringify } from "PFCore/helpers/use_deterministic_stringify";
import { StandardRangeV2 } from "PFCore/types";
import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFormState } from "react-hook-form";

import { AvailabilityCell } from "./availability_cell";
import css from "./availability_table.module.scss";
import { DurationSidePanel } from "./duration_side_panel";
import { transformFromAvailabilityRules, transformToAvailabilityRules } from "./transform_availability_rules";
import { AvailabilityTableData, CalendarDuration } from "./types";
import { useAvailabilityTableScroll } from "./use_availability_table_scroll";
import { useWeekRangeFormatter } from "./use_week_range_formatter";
import {
  buildRangeUpdates,
  clampHours,
  generateWeeks,
  getCalendarDuration,
  WEEK_COLUMN_WIDTH
} from "./utils";

const DEBOUNCE_TIME = 200;

type AvailabilityTableProps = {
  roleFormKeys: RoleFormKey[];
  value: Record<RoleFormKey, StandardRangeV2[]>;
  disabledRoleFormKeys?: RoleFormKey[];
  onRowsChange: (delta: Record<RoleFormKey, StandardRangeV2[]>) => void;
  generateActionsColumn: (
    columnHelper: ColumnHelper<ActivityRow>
  ) => AccessorFnColumnDef<ActivityRow, ActivityRow>;
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  getFixedHeaderWrapperStyle?: () => Partial<CSSStyleDeclaration> | undefined;
  calendarDuration?: CalendarDuration;
  isReadonly?: boolean;
  isOpen?: boolean;
};

export type ActivityRow = Record<number, number | null> & { disabled?: boolean };

export const AvailabilityTable = ({
  roleFormKeys = [],
  value,
  disabledRoleFormKeys = [],
  onRowsChange,
  generateActionsColumn,
  scrollableContainerRef,
  getFixedHeaderWrapperStyle,
  calendarDuration: calendarDurationProp,
  isReadonly = false,
  isOpen
}: AvailabilityTableProps) => {
  const availabilitySidePanelRoleFormKey = useMultiActivityFormStore(
    (store) => store.availabilitySidePanelRoleFormKey
  );
  const { setAvailabilitySidePanelRoleFormKey } = useMultiActivityFormStoreActions();
  const duration = useMultiActivityFormStore((store) => ({
    startDate: store.parentActivity?.metadata?.duration?.start_date,
    endDate: store.parentActivity?.metadata?.duration?.end_date
  }));

  const formatWeekRange = useWeekRangeFormatter();
  // it's important to use here useFormState, useFormContext re-renders all on toggle dirty fields
  const { errors } = useFormState();

  const tableInstanceRef = useRef<TanstackTable<ActivityRow> | null>(null);

  const calendarDuration = useMemo(
    () => calendarDurationProp ?? getCalendarDuration(duration?.startDate, duration?.endDate),
    [calendarDurationProp, duration?.startDate, duration?.endDate]
  );

  const weeks = useMemo(() => generateWeeks(calendarDuration), [calendarDuration]);

  const { scrollToAvailabilityWeek } = useAvailabilityTableScroll({ weeks, scrollableContainerRef, isOpen });

  const buildTableData = useCallback(
    (ranges: Record<RoleFormKey, StandardRangeV2[]>): AvailabilityTableData =>
      mapValues(ranges, (availabilityRules) =>
        merge({}, ...map(availabilityRules, (rule) => transformFromAvailabilityRules(rule, weeks)))
      ),
    [weeks]
  );

  const [availabilityValuesMap, setAvailabilityValuesMap] = useState<AvailabilityTableData>(() =>
    buildTableData(value)
  );

  useEffect(() => {
    setAvailabilityValuesMap(buildTableData(value));
  }, [stringify(value), buildTableData]);

  const lastFlushedRef = useRef<AvailabilityTableData>(buildTableData(value));

  useEffect(() => {
    lastFlushedRef.current = buildTableData(value);
  }, [value, buildTableData]);

  const debouncedFlushRef = useRef<(current: AvailabilityTableData) => void>(() => {});

  useLayoutEffect(() => {
    const flush = debounce((current: AvailabilityTableData) => {
      const delta: Partial<AvailabilityTableData> = {};

      Object.keys(current).forEach((key) => {
        const roleFormKey = key as RoleFormKey;

        if (!isEqual(lastFlushedRef.current[roleFormKey], current[roleFormKey])) {
          delta[roleFormKey] = current[roleFormKey];
        }
      });

      if (Object.keys(delta).length > 0) {
        lastFlushedRef.current = current;

        onRowsChange(transformToAvailabilityRules(delta as AvailabilityTableData, weeks));
      }
    }, DEBOUNCE_TIME);

    debouncedFlushRef.current = flush;

    return () => flush.cancel();
  }, [onRowsChange, weeks]);

  const updateAvailabilityData = useCallback((updatedActivityData: Record<RoleFormKey, ActivityRow>) => {
    setAvailabilityValuesMap((prev) => {
      const merged = merge({}, prev, updatedActivityData);

      debouncedFlushRef.current(merged);

      return merged;
    });
  }, []);

  const handleCellChange = useCallback(
    (roleFormKey: RoleFormKey, weekIndex: number, value?: number) =>
      updateAvailabilityData({
        [roleFormKey]: { [weekIndex]: value || null }
      }),
    [updateAvailabilityData]
  );

  const handleDurationApply = useCallback(
    (roleFormKey: RoleFormKey, availability: StandardRangeV2, applyToAll: boolean) => {
      const transformedRow = transformFromAvailabilityRules(availability, weeks);

      if (applyToAll) {
        const enabledRoleFormKeys = roleFormKeys.filter((key) => !disabledRoleFormKeys.includes(key));
        const allUpdates = enabledRoleFormKeys.reduce((acc, key) => ({ ...acc, [key]: transformedRow }), {});
        updateAvailabilityData(allUpdates);
      } else {
        updateAvailabilityData({ [roleFormKey]: transformedRow });
      }

      scrollToAvailabilityWeek(availability.start);
    },
    [weeks, roleFormKeys, disabledRoleFormKeys, updateAvailabilityData, scrollToAvailabilityWeek]
  );

  const handleFill = useCallback(
    ({ sourceCell, targetRange }: FillEvent) => {
      const sourceRow = tableInstanceRef.current?.getRowModel().rows[sourceCell.rowIndex];
      const clamped = clampHours(sourceRow?.original[sourceCell.colIndex] ?? null);

      updateAvailabilityData(
        buildRangeUpdates(targetRange, weeks.length, roleFormKeys, disabledRoleFormKeys, () => clamped)
      );
    },
    [tableInstanceRef, roleFormKeys, disabledRoleFormKeys, weeks, updateAvailabilityData]
  );

  const handleClear = useCallback(
    ({ targetRange }: ClearEvent) => {
      updateAvailabilityData(
        buildRangeUpdates(targetRange, weeks.length, roleFormKeys, disabledRoleFormKeys, () => null)
      );
    },
    [roleFormKeys, disabledRoleFormKeys, weeks, updateAvailabilityData]
  );

  const handlePaste = useCallback(
    ({ anchor, values }: PasteEvent) => {
      const updates = reduce(
        values,
        (acc, rowValues, rowOffset) => {
          const rowIndex = anchor.rowIndex + rowOffset;
          const minCol = anchor.colIndex;
          const range = { minRow: rowIndex, maxRow: rowIndex, minCol, maxCol: minCol + rowValues.length - 1 };

          return merge(
            acc,
            buildRangeUpdates(range, weeks.length, roleFormKeys, disabledRoleFormKeys, (_, colIndex) =>
              clampHours(rowValues[colIndex - minCol])
            )
          );
        },
        {} as Record<RoleFormKey, ActivityRow>
      );

      updateAvailabilityData(updates);
    },
    [roleFormKeys, disabledRoleFormKeys, weeks, updateAvailabilityData]
  );

  const generateColumns = useCallback(
    (columnHelper: ColumnHelper<ActivityRow>) => {
      const columns = weeks.map((week, weekIndex) => {
        const weekRange = formatWeekRange(week.startDate, week.endDate);

        return columnHelper.accessor((row) => row, {
          id: `week_${weekIndex}`,
          header: () => weekRange,
          meta: {
            getCopyValue: (value: unknown) => String((value as ActivityRow)[weekIndex] ?? "")
          },
          cell: (info) => {
            const roleFormKey = String(roleFormKeys[info.row.index]) as RoleFormKey;
            const value = info.getValue()[weekIndex];
            const { disabled } = info.row.original;
            const error = get(errors, `${roleFormKey}.${FIELD_AVAILABILITY_KEY}.message`, undefined) as
              | string
              | undefined;

            return (
              <AvailabilityCell
                rowIndex={info.row.index}
                colIndex={weekIndex}
                value={value}
                disabled={disabled || isReadonly}
                error={error}
                inputClassName={css.singleLineInput}
                weekRange={weekRange}
                roleFormKey={roleFormKey}
                onChange={(newValue) => handleCellChange(roleFormKey, weekIndex, newValue)}
              />
            );
          },
          size: WEEK_COLUMN_WIDTH,
          enableSorting: false
        });
      });
      columns.push(generateActionsColumn(columnHelper));
      return columns;
    },
    [weeks, handleCellChange, roleFormKeys, errors, isReadonly, formatWeekRange, generateActionsColumn]
  );

  const tableData = useMemo(
    () =>
      entries(availabilityValuesMap).map(([roleFormKey, row]) => ({
        ...row,
        disabled: disabledRoleFormKeys.includes(roleFormKey as RoleFormKey)
      })),
    [stringify(availabilityValuesMap), disabledRoleFormKeys]
  );

  return (
    <>
      <InteractiveTable
        dragAxis={DragAxis.Both}
        tableInstanceRef={tableInstanceRef}
        onFill={handleFill}
        onPaste={handlePaste}
        onClear={handleClear}
        scrollableContainerRef={scrollableContainerRef}
        nonInteractiveLastColumn
      >
        <Table<ActivityRow>
          className={css.table}
          tableData={tableData}
          generateColumns={generateColumns}
          enableSorting={false}
          scrollableContainerRef={scrollableContainerRef}
          hideDistantRows
          displayGrid
          noHover
          disableInternalScrolling
          disableAutoResizing
          tableInstanceRef={tableInstanceRef}
          isInteractive
          enableFixedHeader
          getFixedHeaderWrapperStyle={getFixedHeaderWrapperStyle}
        />
      </InteractiveTable>
      <DurationSidePanel
        show={
          availabilitySidePanelRoleFormKey !== null && roleFormKeys.includes(availabilitySidePanelRoleFormKey)
        }
        roleFormKey={availabilitySidePanelRoleFormKey}
        rolesCount={roleFormKeys.length}
        duration={calendarDuration}
        onClose={() => setAvailabilitySidePanelRoleFormKey(null)}
        onApply={handleDurationApply}
      />
    </>
  );
};
